import React, { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Link } from 'react-router-dom';

import {
  Button,
  ArchiveIcon,
  UnarchiveIcon,
  EditIcon,
  VisibilityOutlinedIcon,
  CheckIcon,
  SettingsOutlinedIcon,
  DownloadIcon,
} from '@occc-app/design-system';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { routeCreator } from '@occc-app/routes/Audience/utils';
import { Audience_Status } from '@occc/graphql';
import { Audience } from '../types';

const MENU_WIDTH = 152;

type AudienceActionsDropdownProps = {
  audience?: Audience;
  loading: boolean;
  error?: ApolloError;
  onActivate?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onExport?: () => void;
  isEditAvailable: boolean;
  isExportAvailable: boolean;
  isArchiveAvailable: boolean;
  isActivateAvailable: boolean;
};

export const AudienceActionsDropdown: React.FC<AudienceActionsDropdownProps> = ({
  audience,
  loading,
  error,
  onActivate,
  onArchive,
  onUnarchive,
  onExport,
  isEditAvailable,
  isExportAvailable,
  isArchiveAvailable,
  isActivateAvailable,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleAction = (callback?: () => void) => {
    if (callback) callback();
    handleClose();
  };

  const isDraft = audience?.status === Audience_Status.Draft;
  const isArchived = audience?.status === Audience_Status.Archived;
  const isActive = audience?.status === Audience_Status.Active;

  const shouldShowEditOption = isEditAvailable && !isActive && !isArchived;

  return (
    <>
      <Button
        aria-controls={open ? 'audience-actions-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        startIcon={<SettingsOutlinedIcon />}
        variant='contained'
        color='secondary'
        disabled={loading || !!error || !audience}
      >
        Options
      </Button>

      <Menu
        id='audience-actions-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: MENU_WIDTH,
            },
          },
        }}
        MenuListProps={{ disablePadding: true }}
      >
        {isActivateAvailable && isDraft && (
          <MenuItem onClick={() => handleAction(onActivate)}>
            <ListItemIcon>
              <CheckIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='Activate' />
          </MenuItem>
        )}

        {shouldShowEditOption ? (
          <MenuItem
            component={Link}
            to={routeCreator.update(audience?.uuid || '')}
            onClick={handleClose}
          >
            <ListItemIcon>
              <EditIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='Edit' />
          </MenuItem>
        ) : (
          <MenuItem
            component={Link}
            to={routeCreator.update(audience?.uuid || '')}
            onClick={handleClose}
          >
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='View Configuration' />
          </MenuItem>
        )}

        {isExportAvailable && (
          <MenuItem onClick={() => handleAction(onExport)}>
            <ListItemIcon>
              <DownloadIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='Export' />
          </MenuItem>
        )}

        {isArchiveAvailable && (
          <>
            <Divider sx={{ my: 0 }} />
            {isArchived ? (
              <MenuItem onClick={() => handleAction(onUnarchive)}>
                <ListItemIcon>
                  <UnarchiveIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary='Unarchive' />
              </MenuItem>
            ) : (
              <MenuItem onClick={() => handleAction(onArchive)}>
                <ListItemIcon>
                  <ArchiveIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary='Archive' />
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
};
